import { useEffect, useRef } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import useApiQuery from 'lib/api/useApiQuery';
import { showAlert } from 'state/actions/site';
import {
  callReviewsKeys,
  callReviewsOverviewRequest,
  FILES_PAGE_SIZE
} from '../api/callReviewsFiles';

const REFRESH_ERROR = 'Could not refresh — showing the last loaded data.';

// a failure only takes over the screen when there is nothing at all to show yet.
// once any page has ever loaded, its rows stay put and the error is reported
// inline instead — see the lastGoodDataRef comment below for why this needs to
// survive a query-key change, not just rely on react-query's own placeholder.
function resolveStatus({ isPending, isError, error, hasData }) {
  if (isError && error?.isNoAccess) return 'no-access';
  if (isError && !hasData) return 'error';
  if (isPending && !hasData) return 'loading';
  return 'ready';
}

/**
 * loads the entry point, which answers with the agent's own files or the agency
 * roster depending on who is asking. the view discriminator comes from the api —
 * nothing here reads the user's profile, agency or position to decide.
 * @param page - 1-indexed page of the current view
 */
export default function useCallReviewsOverview(page) {
  const dispatch = useDispatch();

  const query = useApiQuery(
    callReviewsKeys.overview(page, FILES_PAGE_SIZE),
    callReviewsOverviewRequest({ page, pageSize: FILES_PAGE_SIZE }),
    { placeholderData: keepPreviousData }
  );

  const {
    data,
    isSuccess,
    isError,
    error,
    isFetching,
    isPending,
    failureCount
  } = query;

  // react-query's own placeholder (keepPreviousData) only bridges the gap while
  // the new query key is still pending — once a key settles into error, data goes
  // back to undefined and the placeholder is gone for good. page is part of the
  // key here, so paging while the api is down would otherwise wipe the table
  // instead of just failing to refresh it. mutating a ref during render to cache
  // the latest real success is the pattern React's docs use for exactly this
  // ("remember something across renders without triggering one") — it never
  // fires a second render on its own.
  const lastGoodDataRef = useRef(null);
  if (isSuccess) lastGoodDataRef.current = data;

  const effectiveData = data ?? lastGoodDataRef.current;
  const hasData = Boolean(effectiveData);

  // a 401 is a legitimate answer here ("you cannot read this section"), so it gets
  // its own empty state rather than an alert
  useEffect(() => {
    if (!isError || error?.isNoAccess) return;
    dispatch(showAlert({ message: 'Could not load reports.', code: 'error' }));
  }, [isError, error, dispatch]);

  const staleAfterError = isError && !error?.isNoAccess && hasData;

  return {
    status: resolveStatus({ isPending, isError, error, hasData }),
    view: effectiveData?.view ?? null,
    items: effectiveData?.items ?? [],
    total: effectiveData?.total ?? 0,
    totalPages: effectiveData?.total_pages ?? 0,
    // a fetch with rows already on screen: paging, revalidating, or retrying
    isRefreshing: isFetching && !isPending,
    // failureCount only climbs while the shared retry policy is still trying
    isRetrying: isFetching && failureCount > 0,
    refreshError: staleAfterError ? REFRESH_ERROR : null,
    refetch: query.refetch
  };
}
